import { createContext, useContext } from "react";

/**
 * Registry entries. Sections, cards and rows register themselves on mount (in render order) so the
 * sticky header can build its jump menus and search index without a hand-maintained list.
 */
export type SettingsSectionMeta = {
    id: string;
    titleKey: string;
    iconClass: string;
    /** Translation key of the group heading the section sits under in the section menu. */
    groupKey: string;
};

export type SettingsCardMeta = {
    id: string;
    sectionId: string;
    titleKey: string;
};

export type SettingsRowMeta = {
    id: string;
    /** Supplier model key (or settings key) the row edits; "" for rows that only host a component. */
    name: string;
    titleKey: string;
    detailKey?: string;
    sectionId: string;
    cardId: string;
};

/**
 * Unsaved work that lives outside the supplier model (a workflow being edited in the multi-workflow
 * list) and saves on its own. Registered so the sticky header counts it and its Save and Discard
 * reach it.
 */
export type SettingsPendingChange = {
    /** Unique across the page, e.g. "workflow-<guid>". */
    key: string;
    /** Plain text shown in the unsaved list (a workflow's name), not a translation key. */
    title: string;
    /** DOM id of the row to jump to. */
    rowId: string;
    save: () => void;
    discard: () => void;
};

export type ISettingsPageContext = {
    sections: SettingsSectionMeta[];
    cards: SettingsCardMeta[];
    rows: SettingsRowMeta[];
    registerSection: (meta: SettingsSectionMeta) => () => void;
    registerCard: (meta: SettingsCardMeta) => () => void;
    registerRow: (meta: SettingsRowMeta) => () => void;
    /** Section and card currently under the sticky header, kept up to date while scrolling. */
    activeSectionId: string | null;
    activeCardId: string | null;
    /** Scrolls the main view so the element lands just under the sticky header. */
    jumpTo: (elementId: string) => void;
    /** Jumps to a row and flashes it for a moment so the eye finds it. */
    jumpToRow: (rowId: string) => void;
    highlightedRowId: string | null;
    /** Keys of supplier model / settings fields that differ from the last saved state. */
    editedKeys: string[];
    isEdited: (key: string) => boolean;
    /** Height in px of the sticky header, so jumps and scroll-spy offset by it. */
    headerHeight: number;
    setHeaderHeight: (height: number) => void;
    /** Unsaved work registered by components that save on their own. */
    pending: SettingsPendingChange[];
    registerPending: (change: SettingsPendingChange) => () => void;
};

const noop = () => () => {};

export const SettingsPageContext = createContext<ISettingsPageContext>({
    sections: [],
    cards: [],
    rows: [],
    registerSection: noop,
    registerCard: noop,
    registerRow: noop,
    activeSectionId: null,
    activeCardId: null,
    jumpTo: () => {},
    jumpToRow: () => {},
    highlightedRowId: null,
    editedKeys: [],
    isEdited: () => false,
    headerHeight: 0,
    setHeaderHeight: () => {},
    pending: [],
    registerPending: noop,
});

export const useSettingsPage = () => useContext(SettingsPageContext);

/** Which section a card is being rendered in (set by SettingsSection). */
export const SettingsSectionContext = createContext<{ id: string; titleKey: string } | null>(null);

/** Which card a row is being rendered in (set by SettingsCard). */
export const SettingsCardContext = createContext<{ id: string; titleKey: string } | null>(null);

/** The id of the scroll container the page lives in (App.tsx renders it). */
export const MAIN_VIEW_ID = "mainView";

export const getMainView = (): HTMLElement | null => {
    return typeof document === "undefined" ? null : document.getElementById(MAIN_VIEW_ID);
};
